import { useState, ChangeEvent } from 'react'
import { Button } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { GenerateButton } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { RadioGroup, RadioGroupItem } from '@creanizer/ui'
import { Upload, Plus, BookOpen, Trash2 } from 'lucide-react'
import { useCharacterStore } from '../../../../stores/characterStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { useNameStore } from '../../../../stores/nameStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { useCustomValuesStore } from '../../../../stores/customValuesStore'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCommunityStore } from '../../../../stores/communityStore'
import { useSocietyStore } from '../../../../stores/societyStore'
import type { WikipediaInput } from '@creanizer/types'
import { generateWorldContextText } from '@creanizer/utils'

interface WikipediaFormProps {
  input: WikipediaInput
  onChange: (input: WikipediaInput) => void
  onGenerate: () => void
  isGenerating: boolean
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
}

const DEFAULT_CATEGORIES = [
  { value: 'person', label: '人物伝' },
  { value: 'location', label: '地名・場所' },
  { value: 'event', label: '歴史的事件' },
  { value: 'concept', label: '概念・理論' },
  { value: 'creature', label: '生物・種族' },
  { value: 'technology', label: '技術・道具' },
  { value: 'organization', label: '組織・団体' },
  { value: 'culture', label: '文化・宗教' },
  { value: 'custom', label: 'カスタム' }
]

const DEFAULT_ERAS = [
  { value: 'ancient', label: '古代' },
  { value: 'medieval', label: '中世' },
  { value: 'modern', label: '近代' },
  { value: 'contemporary', label: '現代' },
  { value: 'future', label: '未来' },
  { value: 'custom', label: 'カスタム' }
]

export function WikipediaForm({
  input,
  onChange,
  onGenerate,
  isGenerating,
  useStreaming = false,
  onStreamingChange
}: WikipediaFormProps) {
  const { characters } = useCharacterStore()
  const { savedPlots } = usePlotStore()
  const { savedRelationships } = useRelationshipStore()
  const { savedNames } = useNameStore()
  const { savedTitles } = useTitleStore()
  const { savedWorlds: worlds } = useWorldBuilderStore()
  const { savedCommunities } = useCommunityStore()
  const { savedSocieties } = useSocietyStore()
  const { getCustomValues, addCustomValue, deleteCustomValue, useCustomValue } = useCustomValuesStore()
  
  const [inputMode, setInputMode] = useState<'standard' | 'idea' | 'reference'>('standard')
  const [customCategory, setCustomCategory] = useState('')
  const [customEra, setCustomEra] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState<string>('')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      const text = event.target?.result as string
      onChange({
        ...input,
        ideaFile: text
        // ideaTextは消さない - 両方使えるようにする
      })
      setUploadedFileName(file.name)
    }
    reader.readAsText(file)
  }

  const clearFile = () => {
    onChange({
      ...input,
      ideaFile: undefined
    })
    setUploadedFileName('')
    // ファイル入力をリセット
    const fileInput = document.getElementById('file-upload') as HTMLInputElement
    if (fileInput) {
      fileInput.value = ''
    }
  }

  const handleWorldSelect = (worldId: string) => {
    if (!worldId || worldId === 'none') {
      setSelectedWorldId('')
      onChange({
        ...input,
        worldContext: undefined
      })
      return
    }

    setSelectedWorldId(worldId)
    const world = worlds.find(w => w.id === worldId)
    if (!world) return

    const context = generateWorldContextText(world.result)

    onChange({
      ...input,
      worldContext: context
    })
  }

  const handleAddCustomCategory = async () => {
    if (!customCategory.trim()) return
    await addCustomValue('category', customCategory, customCategory, 'wikipedia')
    alert(`カテゴリ「${customCategory}」を保存しました`)
  }

  const handleAddCustomEra = async () => {
    if (!customEra.trim()) return
    await addCustomValue('era', customEra, customEra, 'wikipedia')
    alert(`時代設定「${customEra}」を保存しました`)
  }

  const handleTabChange = (value: string) => {
    setInputMode(value as 'standard' | 'idea' | 'reference')
    
    // タブ切り替え時に他の入力をクリア
    if (value === 'standard') {
      onChange({
        ...input,
        ideaText: undefined,
        ideaFile: undefined,
        savedCharacterId: undefined,
        savedPlotId: undefined,
        savedRelationshipId: undefined,
        savedNameIds: undefined,
        savedTitleId: undefined,
        savedCommunityId: undefined,
        savedSocietyId: undefined,
        referenceContext: undefined  // 参照データもクリア
      })
      setUploadedFileName('')
    } else if (value === 'idea') {
      onChange({
        ...input,
        keyword: undefined,
        savedCharacterId: undefined,
        savedPlotId: undefined,
        savedRelationshipId: undefined,
        savedNameIds: undefined,
        savedTitleId: undefined,
        savedCommunityId: undefined,
        savedSocietyId: undefined,
        referenceContext: undefined  // 参照データもクリア
      })
    } else if (value === 'reference') {
      onChange({
        ...input,
        keyword: undefined,
        ideaText: undefined,
        ideaFile: undefined
        // referenceContextは参照モードで使うのでクリアしない
      })
      setUploadedFileName('')
    }
  }

  const customCategories = getCustomValues('category', 'wikipedia')
  const customEras = getCustomValues('era', 'wikipedia')

  return (
    <div className="space-y-6">
      <div>
        <Label>入力方式</Label>
        <Tabs value={inputMode} onValueChange={handleTabChange} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="standard">標準</TabsTrigger>
            <TabsTrigger value="idea">アイディア</TabsTrigger>
            <TabsTrigger value="reference">参照</TabsTrigger>
          </TabsList>

          <TabsContent value="standard" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="keyword">キーワード</Label>
              <Input
                id="keyword"
                value={input.keyword || ''}
                onChange={(e) => onChange({ ...input, keyword: e.target.value })}
                placeholder="記事の主題を入力"
              />
            </div>
          </TabsContent>

          <TabsContent value="idea" className="space-y-4 mt-4">
            <div>
              <Label htmlFor="ideaText">アイディア入力</Label>
              <Textarea
                id="ideaText"
                value={input.ideaText || ''}
                onChange={(e) => onChange({ ...input, ideaText: e.target.value })}
                placeholder="自由にアイディアを入力してください"
                rows={5}
              />
            </div>
            
            <div className="space-y-2">
              <Label>ファイルアップロード</Label>
              
              {uploadedFileName && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Upload className="h-4 w-4 text-blue-600" />
                    <span className="text-sm">
                      ファイル: <strong>{uploadedFileName}</strong>
                    </span>
                  </div>
                  <Button 
                    size="sm" 
                    variant="ghost"
                    onClick={clearFile}
                  >
                    クリア
                  </Button>
                </div>
              )}
              
              <input
                id="file-upload"
                type="file"
                accept=".txt,.md"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                variant="outline"
                onClick={() => document.getElementById('file-upload')?.click()}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploadedFileName ? 'ファイルを変更' : 'ファイルを選択'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="reference" className="space-y-4 mt-4">
            <div>
              <Label>保存済みキャラクター</Label>
              <Select value={input.savedCharacterId || ''} onValueChange={(value) => {
                const character = characters.find(c => c.id === value)
                if (!character) return
                
                const characterContext = `
【キャラクター情報】
名前: ${character.result.name}
${character.result.summary ? `概要: ${character.result.summary}` : ''}
${character.result.personality ? `性格: ${typeof character.result.personality === 'string' ? character.result.personality : character.result.personality.traits?.join('、') || ''}` : ''}
${character.result.background ? `背景: ${typeof character.result.background === 'string' ? character.result.background : `${character.result.background.birthplace || ''} ${character.result.background.occupation || ''}`}` : ''}
${character.result.appearance ? `外見: ${typeof character.result.appearance === 'string' ? character.result.appearance : character.result.appearance.overallImpression || ''}` : ''}
                `.trim()
                
                onChange({ 
                  ...input, 
                  savedCharacterId: value,
                  referenceContext: characterContext
                })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="キャラクターを選択" />
                </SelectTrigger>
                <SelectContent>
                  {characters.map((char) => (
                    <SelectItem key={char.id} value={char.id}>
                      {char.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>保存済みプロット</Label>
              <Select value={input.savedPlotId || ''} onValueChange={(value) => {
                const plot = savedPlots.find(p => p.id === value)
                if (!plot) return
                
                const plotContext = `
【プロット情報】
タイトル: ${plot.result.title}
テーマ: ${plot.result.themes.join('、')}

あらすじ:
${plot.result.synopsis}

主要キャラクター:
${plot.result.characters.map(c => `- ${c.name}: ${c.role} - ${c.description || ''}`).join('\n')}
                `.trim()
                
                onChange({ 
                  ...input, 
                  savedPlotId: value,
                  referenceContext: plotContext
                })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="プロットを選択" />
                </SelectTrigger>
                <SelectContent>
                  {savedPlots.map((plot) => (
                    <SelectItem key={plot.id} value={plot.id}>
                      {plot.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>保存済みコミュニティ</Label>
              <Select value={input.savedCommunityId || ''} onValueChange={(value) => {
                const community = savedCommunities.find(c => c.id === value)
                if (!community) return
                
                const communityContext = `
【コミュニティ情報】
名前: ${community.result.basicInfo.name}
種類: ${community.result.basicInfo.type}
規模: ${community.result.basicInfo.scale}
人口: ${community.result.basicInfo.population}人
評判: ${community.result.basicInfo.reputation}

【雰囲気】
${community.result.basicInfo.atmosphere}

【特徴】
地理: ${community.result.features.geography}
文化: ${community.result.features.culture}
経済: ${community.result.features.economy}
政治: ${community.result.features.politics}

【住民の特徴】
${community.result.residents.characteristics}

【主要な問題】
${community.result.features.problems}

【創立物語】
${community.result.history.foundingStory}
                `.trim()
                
                onChange({ 
                  ...input, 
                  savedCommunityId: value,
                  referenceContext: communityContext
                })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="コミュニティを選択" />
                </SelectTrigger>
                <SelectContent>
                  {savedCommunities.map((community) => (
                    <SelectItem key={community.id} value={community.id}>
                      {community.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>保存済みソサエティ</Label>
              <Select value={input.savedSocietyId || ''} onValueChange={(value) => {
                const society = savedSocieties.find(s => s.id === value)
                if (!society) return
                
                const societyContext = `
【ソサエティ情報】
名前: ${society.result.basicInfo.name}
${society.result.basicInfo.aliases ? `別名: ${society.result.basicInfo.aliases.join('、')}` : ''}
種類: ${society.result.basicInfo.type}
規模: ${society.result.basicInfo.scale}
${society.result.basicInfo.motto ? `モットー: ${society.result.basicInfo.motto}` : ''}

【概要】
${society.result.basicInfo.summary}

【詳細説明】
${society.result.basicInfo.description}

【目的・理念】
${society.result.basicInfo.purpose}

【公的イメージ】
${society.result.basicInfo.publicImage}
${society.result.basicInfo.actualNature ? `\n【実際の性質】\n${society.result.basicInfo.actualNature}` : ''}

【組織構造】
メンバー数: ${society.result.structure.memberCount}
意思決定: ${society.result.structure.decisionMaking}
新規獲得: ${society.result.structure.recruitmentMethod}
${society.result.structure.promotionSystem ? `昇進システム: ${society.result.structure.promotionSystem}` : ''}

【活動内容】
${society.result.features.activities}

【組織文化】
${society.result.features.culture}

【他組織との関係】
${society.result.features.relations}
${society.result.features.secrets ? `\n【秘密・裏設定】\n${society.result.features.secrets}` : ''}
                `.trim()
                
                onChange({ 
                  ...input, 
                  savedSocietyId: value,
                  referenceContext: societyContext
                })
              }}>
                <SelectTrigger>
                  <SelectValue placeholder="ソサエティを選択" />
                </SelectTrigger>
                <SelectContent>
                  {savedSocieties.map((society) => (
                    <SelectItem key={society.id} value={society.id}>
                      {society.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>カテゴリ</Label>
          <Select value={input.category || ''} onValueChange={(value) => onChange({ ...input, category: value })}>
            <SelectTrigger>
              <SelectValue placeholder="カテゴリを選択" />
            </SelectTrigger>
            <SelectContent>
              {DEFAULT_CATEGORIES.map((cat) => (
                <SelectItem key={cat.value} value={cat.value}>
                  {cat.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {input.category === 'custom' && (
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <Input
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value)}
                  placeholder="カスタムカテゴリを入力"
                />
                <Button onClick={handleAddCustomCategory} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {customCategories.length > 0 && (
                <div className="space-y-1">
                  {customCategories.map((custom) => (
                    <div key={custom.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          useCustomValue(custom.id)
                          onChange({ ...input, customCategory: custom.value })
                        }}
                      >
                        {custom.label}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCustomValue(custom.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div>
          <Label>時代設定</Label>
          <Select value={input.era || ''} onValueChange={(value) => onChange({ ...input, era: value })}>
            <SelectTrigger>
              <SelectValue placeholder="時代を選択" />
            </SelectTrigger>
            <SelectContent>
              {DEFAULT_ERAS.map((era) => (
                <SelectItem key={era.value} value={era.value}>
                  {era.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {input.era === 'custom' && (
            <div className="mt-2 space-y-2">
              <div className="flex gap-2">
                <Input
                  value={customEra}
                  onChange={(e) => setCustomEra(e.target.value)}
                  placeholder="カスタム時代を入力"
                />
                <Button onClick={handleAddCustomEra} size="sm">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {customEras.length > 0 && (
                <div className="space-y-1">
                  {customEras.map((custom) => (
                    <div key={custom.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          useCustomValue(custom.id)
                          onChange({ ...input, customEra: custom.value })
                        }}
                      >
                        {custom.label}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteCustomValue(custom.id)}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {worlds.length > 0 && (
        <div>
          <Label>世界観の選択（オプション）</Label>
          <Select value={selectedWorldId || 'none'} onValueChange={handleWorldSelect}>
            <SelectTrigger>
              <SelectValue placeholder="世界観を選択すると、その世界の記事として生成されます" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">選択しない</SelectItem>
              {worlds.map((world) => (
                <SelectItem key={world.id} value={world.id}>
                  {world.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {input.worldContext && (
            <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-600">
              世界観が選択されています
            </div>
          )}
        </div>
      )}

      <div>
        <Label>詳細度レベル ({input.detailLevel || 5}/10)</Label>
        <Slider
          value={[input.detailLevel || 5]}
          onValueChange={([value]) => onChange({ ...input, detailLevel: value })}
          min={1}
          max={10}
          step={1}
        />
      </div>

      <div>
        <Label>記事の長さ</Label>
        <RadioGroup
          value={input.articleLength || 'medium'}
          onValueChange={(value: 'short' | 'medium' | 'long') => onChange({ ...input, articleLength: value })}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="short" id="short" />
            <Label htmlFor="short">短編（3-4セクション）</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="medium" id="medium" />
            <Label htmlFor="medium">中編（5-7セクション）</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="long" id="long" />
            <Label htmlFor="long">長編（8-10セクション）</Label>
          </div>
        </RadioGroup>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <Checkbox
            id="useProMode"
            checked={input.useProMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useProMode: !!checked })}
          />
          <Label htmlFor="useProMode">Proモード（より詳細で高品質）</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="useGemini3FlashMode"
            checked={input.useGemini3FlashMode || false}
            onCheckedChange={(checked) => onChange({ ...input, useGemini3FlashMode: !!checked })}
          />
          <Label htmlFor="useGemini3FlashMode">Gemini 3 Flash モード（試験版）</Label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox
            id="useStreaming"
            checked={useStreaming}
            onCheckedChange={(checked) => onStreamingChange?.(!!checked)}
          />
          <Label htmlFor="useStreaming">ストリーミングモード（リアルタイム生成）</Label>
        </div>
        {useStreaming && (
          <div className="ml-6 text-sm text-muted-foreground">
            生成中のテキストをリアルタイムで表示します
          </div>
        )}
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="additionalSettings">追加設定（任意）</Label>
          <span className="text-xs text-muted-foreground">
            {input.additionalSettings ? `${input.additionalSettings.length}文字` : ''}
          </span>
        </div>
        <Textarea
          id="additionalSettings"
          value={input.additionalSettings || ''}
          onChange={(e) => onChange({ ...input, additionalSettings: e.target.value })}
          placeholder="記事に関する追加の要望や設定があれば記入してください&#10;例：&#10;・「〇〇についての記事を書く」（特定の対象を指定）&#10;・特定の視点から記事を書く（批判的、賞賛的など）&#10;・必ず含めたい情報や要素&#10;・記事のトーンやスタイル（学術的、一般向けなど）&#10;・参照データから特に詳しく書いてほしい部分"
          rows={5}
        />
      </div>

      <GenerateButton onClick={onGenerate} isLoading={isGenerating} />
    </div>
  )
}